import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Copy, FilePdf, CreditCard, ArrowsClockwise, Trash, UploadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;
const errMsg = (e) => { const d = e.response?.data?.detail; return Array.isArray(d) ? d.map((x) => x.msg).join(" ") : d || "Errore"; };
const copy = async (t) => { try { await navigator.clipboard.writeText(t); toast.success("Copiato"); } catch { toast.error("Copia non riuscita"); } };

function Box({ title, children, testid, right }) {
  return (
    <div className="rounded-2xl border border-line bg-canvas p-5" data-testid={testid}>
      <div className="flex justify-between items-center mb-3 gap-2 flex-wrap"><div className="font-serif text-lg text-ink">{title}</div>{right}</div>
      {children}
    </div>
  );
}

function Kpi({ l, v, testid }) {
  return <div className="rounded-2xl border border-line bg-canvas p-4" data-testid={testid}><div className="text-xs uppercase tracking-widest text-stone">{l}</div><div className="font-serif text-3xl text-ink mt-1">{v}</div></div>;
}

function Billing({ d, reload }) {
  const pr = d.pricing; const b = d.partner.billing || {};
  const checkout = async () => { try { const r = await api.post("/partner/billing/checkout"); window.location.href = r.data.url; } catch (e) { toast.error(errMsg(e)); } };
  const sync = async () => { try { const r = await api.post("/partner/billing/sync"); toast.success(`Aggiornato: ${r.data.quantity} strutture a ${r.data.unit_price} €`); reload(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <Box title="Fatturazione unica" testid="partner-billing">
      <div className="grid sm:grid-cols-3 gap-3 text-sm">
        <div>Strutture attive<div className="font-serif text-2xl" data-testid="partner-billing-count">{pr.active_properties}</div></div>
        <div>Prezzo per struttura<div className="font-serif text-2xl" data-testid="partner-billing-unit">{pr.unit_price} €{pr.pilot && <span className="text-xs text-gold ml-2">pilota</span>}</div></div>
        <div>Totale mensile<div className="font-serif text-2xl" data-testid="partner-billing-total">{pr.monthly_total} €</div></div>
      </div>
      <div className="flex flex-wrap gap-1.5 mt-4">{pr.tiers.map((t) => <span key={t.min} className={`text-xs px-2 py-1 rounded-full border ${!pr.pilot && pr.unit_price === t.price ? "bg-ink text-canvas border-ink" : "border-line text-stone"}`}>{t.min}+ · {t.price} €</span>)}</div>
      <div className="text-xs text-stone mt-3">Monte minuti voce condiviso: {pr.voice_pool_minutes} min/mese · IVA esclusa{pr.pilot && pr.pilot_until ? ` · pilota fino al ${new Date(pr.pilot_until).toLocaleDateString("it-IT")}` : ""}</div>
      <div className="mt-4">
        {b.status === "active"
          ? <Button variant="outline" className="rounded-full" onClick={sync} data-testid="partner-billing-sync"><ArrowsClockwise size={16} className="mr-1" />Aggiorna abbonamento al numero di strutture</Button>
          : <Button className="rounded-full bg-ink text-canvas hover:bg-olive" onClick={checkout} disabled={!pr.active_properties} data-testid="partner-billing-checkout"><CreditCard size={16} className="mr-1" />Attiva fatturazione</Button>}
      </div>
    </Box>
  );
}

function AddProperties({ reload }) {
  const [f, setF] = useState({ property_name: "", property_type: "hotel", owner_name: "", owner_email: "" });
  const [csv, setCsv] = useState("");
  const add = async (e) => {
    e.preventDefault();
    try { await api.post("/partner/properties", f); toast.success("Struttura aggiunta: invito inviato al titolare"); setF({ ...f, property_name: "", owner_name: "", owner_email: "" }); reload(); } catch (err) { toast.error(errMsg(err)); }
  };
  const importCsv = async () => {
    try { const r = await api.post("/partner/properties/import", { csv }); toast.success(`${r.data.created} strutture importate`); if (r.data.errors.length) toast.error(r.data.errors.slice(0, 3).join(" · ")); setCsv(""); reload(); } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <Box title="Aggiungi strutture" testid="partner-add">
      <form onSubmit={add} className="grid md:grid-cols-5 gap-2">
        <Input required placeholder="Nome struttura" value={f.property_name} onChange={(e) => setF({ ...f, property_name: e.target.value })} data-testid="partner-add-name" />
        <Input placeholder="Tipo (hotel, b&b…)" value={f.property_type} onChange={(e) => setF({ ...f, property_type: e.target.value })} data-testid="partner-add-type" />
        <Input required placeholder="Titolare" value={f.owner_name} onChange={(e) => setF({ ...f, owner_name: e.target.value })} data-testid="partner-add-owner" />
        <Input required type="email" placeholder="Email titolare" value={f.owner_email} onChange={(e) => setF({ ...f, owner_email: e.target.value })} data-testid="partner-add-email" />
        <Button className="rounded-full bg-ink text-canvas hover:bg-olive" data-testid="partner-add-submit">Aggiungi</Button>
      </form>
      <div className="mt-4 text-xs text-stone">Import da CSV, una riga per struttura: <code>nome,tipo,titolare,email</code></div>
      <Textarea rows={3} className="mt-2 text-xs" placeholder={"Hotel Sole,hotel,Mario Rossi,mario@hotelsole.it\nB&B Luna,bnb,Anna Bianchi,anna@bbluna.it"} value={csv} onChange={(e) => setCsv(e.target.value)} data-testid="partner-csv" />
      <Button variant="outline" className="rounded-full mt-2" disabled={!csv.trim()} onClick={importCsv} data-testid="partner-csv-import"><UploadSimple size={16} className="mr-1" />Importa</Button>
    </Box>
  );
}

function PropertyTable({ rows, onRemove, prefix }) {
  if (!rows.length) return <div className="text-sm text-stone" data-testid={`${prefix}-empty`}>Nessuna struttura ancora.</div>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" data-testid={`${prefix}-table`}>
        <thead><tr className="text-left text-xs uppercase tracking-widest text-stone"><th className="py-2 pr-3">Struttura</th><th className="pr-3">Titolare</th><th className="pr-3">Stato</th><th className="pr-3">Messaggi</th><th className="pr-3">Prenot.</th><th className="pr-3">Chiamate</th><th className="pr-3">Min voce</th><th /></tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.id} className="border-t border-line" data-testid={`${prefix}-row-${i}`}>
              <td className="py-2 pr-3 font-medium text-ink">{r.property_name}</td>
              <td className="pr-3 text-stone">{r.owner_name}<div className="text-xs">{r.email}</div></td>
              <td className="pr-3 text-xs">{r.invite_pending ? "Invito inviato" : (r.subscription?.status === "active" ? "Attiva" : r.subscription?.status || "—")}</td>
              <td className="pr-3">{r.messages}</td><td className="pr-3">{r.bookings}</td><td className="pr-3">{r.calls}</td><td className="pr-3">{r.voice_minutes}</td>
              <td>{onRemove && <button onClick={() => onRemove(r)} className="text-stone hover:text-terracotta" data-testid={`${prefix}-remove-${i}`}><Trash size={14} /></button>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Branding({ d, reload }) {
  const [f, setF] = useState(d.partner.branding || { brand_name: "", logo_url: "", cobrand_enabled: true });
  const save = async () => { try { await api.put("/partner/branding", f); toast.success("Co-branding salvato"); reload(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <Box title="Co-branding: Otelya × il tuo marchio" testid="partner-branding">
      <div className="grid md:grid-cols-3 gap-2 items-center">
        <Input placeholder="Nome mostrato" value={f.brand_name} onChange={(e) => setF({ ...f, brand_name: e.target.value })} data-testid="partner-brand-name" />
        <Input placeholder="Link logo https://… (PNG/SVG)" value={f.logo_url} onChange={(e) => setF({ ...f, logo_url: e.target.value })} data-testid="partner-brand-logo" />
        <label className="flex items-center gap-2 text-sm"><Switch checked={f.cobrand_enabled} onCheckedChange={(v) => setF({ ...f, cobrand_enabled: v })} data-testid="partner-brand-toggle" />Mostra alle strutture</label>
      </div>
      <div className="mt-3 flex items-center gap-3 text-sm text-stone">Anteprima: <span className="font-serif text-ink">Otelya</span> × {f.logo_url ? <img src={f.logo_url} alt="" className="h-6" /> : null}<b className="text-ink">{f.brand_name}</b></div>
      <Button className="rounded-full mt-3" onClick={save} data-testid="partner-brand-save">Salva</Button>
    </Box>
  );
}

function RankCard({ title, items, tone, prefix, onResend }) {
  return (
    <div className="rounded-2xl border border-line bg-canvas p-5" data-testid={prefix}>
      <div className="font-serif text-lg text-ink mb-3">{title}</div>
      {!items.length ? <div className="text-sm text-stone">Servono almeno 2 strutture.</div> : items.map((r, i) => (
        <div key={r.id} className="py-3 border-b border-line last:border-0" data-testid={`${prefix}-${i}`}>
          <div className="flex justify-between gap-2 text-sm"><b className="text-ink">{r.property_name}</b><span className={tone}>{r.messages} msg · {r.bookings} pren. · {r.calls} chiam.</span></div>
          <ul className="mt-1 text-xs text-stone space-y-0.5">{r.tips.map((t) => <li key={t}>→ {t}</li>)}</ul>
          {r.invite_pending && onResend && <button className="text-xs underline text-olive mt-1" onClick={() => onResend(r)} data-testid={`${prefix}-resend-${i}`}>Reinvia invito</button>}
        </div>
      ))}
    </div>
  );
}

function Ranking({ d }) {
  const resend = async (r) => { try { await api.post(`/partner/properties/${r.id}/resend-invite`); toast.success("Invito reinviato"); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="grid md:grid-cols-2 gap-4" data-testid="partner-ranking">
      <RankCard title="Strutture più attive (30 giorni)" items={d.ranking.top} tone="text-olive" prefix="partner-rank-top" onResend={resend} />
      <RankCard title="Da aiutare: meno attive e consigli" items={d.ranking.bottom} tone="text-terracotta" prefix="partner-rank-bottom" onResend={resend} />
    </div>
  );
}

export default function PartnerConsole() {
  const [d, setD] = useState(null);
  const load = () => api.get("/partner/me").then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => {
    const sid = new URLSearchParams(window.location.search).get("partner_checkout");
    if (sid && sid !== "cancelled") api.get(`/partner/billing/confirm?session_id=${sid}`).then((r) => r.data.status === "active" && toast.success("Fatturazione attivata")).finally(load);
    else load();
  }, []);
  if (!d) return <div className="text-stone" data-testid="partner-loading">Caricamento…</div>;
  const p = d.partner;
  if (p.status !== "approved") return (
    <Box title={p.status === "rejected" ? "Richiesta non approvata" : p.status === "revoked" ? "Account Partner revocato" : "Richiesta in revisione"} testid="partner-pending">
      <p className="text-sm text-stone">{p.status === "rejected" ? "Scrivici a alfio@otelya.app per maggiori informazioni." : p.status === "revoked" ? `L'accesso alla Console è stato revocato. Le tue strutture restano attive fino al ${p.properties_active_until ? new Date(p.properties_active_until).toLocaleDateString("it-IT") : "fine mese"}. Per informazioni: alfio@otelya.app.` : "Approviamo gli account Partner entro 24 ore lavorative. Riceverai un'email appena la console è attiva."}</p>
      <a href={`${API}/api/partner/proposal.pdf`} target="_blank" rel="noreferrer"><Button variant="outline" className="rounded-full mt-3" data-testid="partner-pending-pdf"><FilePdf size={16} className="mr-1" />Proposta Partner (PDF)</Button></a>
    </Box>
  );
  const remove = async (r) => { if (!window.confirm(`Rimuovere ${r.property_name} dalla tua rete?`)) return; await api.delete(`/partner/properties/${r.id}`); load(); };
  const downloadReport = async () => {
    try {
      const r = await api.get("/partner/report.pdf", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url; a.download = "Otelya-Report.pdf"; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch { toast.error("Report non disponibile"); }
  };
  return (
    <div className="space-y-6" data-testid="partner-console">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="text-sm text-stone">{p.company_name} · modello <b className="text-ink">{d.model_label}</b></div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" className="rounded-full" onClick={downloadReport} data-testid="partner-console-report"><FilePdf size={14} className="mr-1" />Report mese scorso</Button>
          <a href={`${API}/api/partner/proposal.pdf`} target="_blank" rel="noreferrer"><Button variant="outline" size="sm" className="rounded-full" data-testid="partner-console-pdf"><FilePdf size={14} className="mr-1" />Proposta PDF</Button></a>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Kpi l="Strutture" v={d.managed.length + d.referred.length} testid="partner-kpi-properties" />
        <Kpi l="Messaggi 30gg" v={d.totals.messages} testid="partner-kpi-messages" />
        <Kpi l="Prenotazioni 30gg" v={d.totals.bookings} testid="partner-kpi-bookings" />
        <Kpi l="Chiamate 30gg" v={d.totals.calls} testid="partner-kpi-calls" />
        <Kpi l="Minuti voce 30gg" v={d.totals.voice_minutes} testid="partner-kpi-minutes" />
      </div>
      {d.ranking && <Ranking d={d} />}
      {p.model === "referral" ? (
        <>
          <Box title="Il tuo link di segnalazione" testid="partner-referral">
            <div className="flex gap-2"><Input readOnly value={d.referral.link} data-testid="partner-referral-link" /><Button onClick={() => copy(d.referral.link)} className="rounded-full" data-testid="partner-referral-copy"><Copy size={14} /></Button></div>
            <div className="text-sm text-stone mt-3">Commissione {d.referral.commission_pct}% ricorrente · maturata questo mese: <b className="text-ink" data-testid="partner-commission">{d.referral.monthly_commission} €</b></div>
          </Box>
          <Box title="Strutture segnalate" testid="partner-referred"><PropertyTable rows={d.referred} prefix="partner-referred" /></Box>
        </>
      ) : (
        <>
          <Billing d={d} reload={load} />
          <AddProperties reload={load} />
          <Box title="Le tue strutture" testid="partner-managed"><PropertyTable rows={d.managed} onRemove={remove} prefix="partner-managed" /></Box>
          {p.model === "cobrand" && <Branding d={d} reload={load} />}
        </>
      )}
    </div>
  );
}
